import { useEffect, useState } from 'react';
import type { AgentDetails } from '@atlassian/conversation-assistant-service';
import { useBoardSelector } from '../../../state/index.tsx';
import {
	getFirstTransitionIdFromColumn,
	projectIdSelector,
} from '../../../state/selectors/software/software-selectors.tsx';
import { firstIssueTypeIdSelector } from '../../../state/selectors/issue-type/issue-type-selectors.tsx';
import { useWorkflows } from '../../../rest/workflow/index.tsx';
import { getAgentIdFromTransitionRule } from './get-agent-id-from-transition-rule.tsx';
import { fetchAgentDetailsById } from './fetch-agent-details-by-id.tsx';

/**
 * @deprecated Do not use, temporary hook to be cleaned up post Team EU 25
 * Returns the agentDetails if the column's first transition has a trigger agent rule, else return null
 */
export const useAgentDetailsFromColumn = (columnId: string) => {
	const firstIssueTypeId = useBoardSelector(firstIssueTypeIdSelector);
	const projectId = useBoardSelector(projectIdSelector)?.toString();
	const transitionId = useBoardSelector((state) => getFirstTransitionIdFromColumn(state)(columnId));
	const { workflows = [], loading, error } = useWorkflows(projectId, firstIssueTypeId || '') || {};
	const [agentId, setAgentId] = useState<string | null>(null);
	const [agentDetails, setAgentDetails] = useState<AgentDetails | null>(null);

	// Effect to derive agentId from workflow/transition
	useEffect(() => {
		if (!transitionId || !workflows || workflows.length === 0 || error || loading) {
			setAgentId(null);
			return;
		}
		const newAgentId = getAgentIdFromTransitionRule({ transitionId, workflow: workflows[0] });
		setAgentId(newAgentId);
	}, [transitionId, workflows, error, loading]);

	// Effect to fetch agent details from agentId
	useEffect(() => {
		if (!agentId) {
			setAgentDetails(null);
			return;
		}
		const fetchDetails = async () => {
			try {
				const details = await fetchAgentDetailsById(agentId);
				setAgentDetails(details);
			} catch {
				setAgentDetails(null);
			}
		};
		fetchDetails();
	}, [agentId]);

	return { agentDetails };
};
